import { useState, useEffect, useRef } from 'react';
import PuzzleEngineV2 from '../components/puzzle/PuzzleEngineV2';

/**
 * TestPuzzle - A test page for the PuzzleEngineV2 component.
 * Upload any image and test with different grid sizes.
 */
function TestPuzzle() {
  const [imageData, setImageData] = useState(null);
  const [rows, setRows] = useState(3);
  const [columns, setColumns] = useState(3);
  const [pieceStyle, setPieceStyle] = useState('traditional');
  const [containerWidth, setContainerWidth] = useState(800);
  const [isComplete, setIsComplete] = useState(false);
  const [key, setKey] = useState(0); // For resetting puzzle
  const containerRef = useRef(null);

  // Measure container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setImageData(event.target.result);
      setIsComplete(false);
      setKey((k) => k + 1);
    };
    reader.readAsDataURL(file);
  };

  const handleComplete = () => {
    setIsComplete(true);
  };

  const handleReset = () => {
    setKey((k) => k + 1);
    setIsComplete(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" ref={containerRef}>
      <h1 className="text-3xl font-bold text-puzzle-text mb-4">
        SimplePuzzle Test
      </h1>
      <p className="text-puzzle-text-secondary mb-6">
        This tests direct image slicing (no headbreaker, no tiling possible).
      </p>

      {/* Controls */}
      <div className="bg-puzzle-surface rounded-lg p-4 mb-6 space-y-4">
        <div>
          <label htmlFor="image-upload" className="block text-sm font-medium text-puzzle-text mb-2">
            Upload Image
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="mt-1 block w-full text-sm text-puzzle-text file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-puzzle-blue file:text-white hover:file:bg-blue-700"
            />
          </label>
        </div>

        <div className="flex gap-4">
          <label htmlFor="rows-select" className="flex-1">
            <span className="block text-sm font-medium text-puzzle-text mb-1">Rows</span>
            <select
              id="rows-select"
              value={rows}
              onChange={(e) => {
                setRows(Number(e.target.value));
                setKey((k) => k + 1);
              }}
              className="w-full px-3 py-2 rounded-lg bg-puzzle-muted text-puzzle-text"
            >
              {[2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>

          <label htmlFor="cols-select" className="flex-1">
            <span className="block text-sm font-medium text-puzzle-text mb-1">Columns</span>
            <select
              id="cols-select"
              value={columns}
              onChange={(e) => {
                setColumns(Number(e.target.value));
                setKey((k) => k + 1);
              }}
              className="w-full px-3 py-2 rounded-lg bg-puzzle-muted text-puzzle-text"
            >
              {[2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>

          <label htmlFor="style-select" className="flex-1">
            <span className="block text-sm font-medium text-puzzle-text mb-1">Piece Style</span>
            <select
              id="style-select"
              value={pieceStyle}
              onChange={(e) => {
                setPieceStyle(e.target.value);
                setKey((k) => k + 1);
              }}
              className="w-full px-3 py-2 rounded-lg bg-puzzle-muted text-puzzle-text"
            >
              <option value="traditional">Traditional</option>
              <option value="rounded">Rounded</option>
              <option value="square">Square</option>
            </select>
          </label>
        </div>

        {imageData && (
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 bg-puzzle-blue text-white rounded-lg hover:bg-blue-700"
          >
            Shuffle Again
          </button>
        )}
      </div>

      {/* Completion message */}
      {isComplete && (
        <div className="bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg p-4 mb-6">
          <p className="text-green-700 dark:text-green-300 font-medium">
            Puzzle Complete! All pieces placed correctly.
          </p>
        </div>
      )}

      {/* Puzzle */}
      {imageData ? (
        <PuzzleEngineV2
          key={key}
          imageData={imageData}
          rows={rows}
          columns={columns}
          pieceStyle={pieceStyle}
          containerWidth={containerWidth}
          onComplete={handleComplete}
        />
      ) : (
        <div className="bg-puzzle-muted rounded-lg p-12 text-center text-puzzle-text-secondary">
          Upload an image to start testing
        </div>
      )}
    </div>
  );
}

export default TestPuzzle;
